/**
 * Cart export functionality
 * @module shared-cart/export
 */

import { CART_EXPORT_IGNORED_DOMAINS, SISTER_SITE_DOMAINS } from '../constants.js';
import { base64UrlEncode } from '../utils/base64.js';
import { getCart, getActiveCartDiscounts, clearCart } from '../cart/api.js';
import { backupCart } from '../cart/backup.js';
import type { CartState } from '../cart/api.js';

interface HandoffItem {
  id: string;
  qty: number | undefined;
  gwp?: true;
}

interface HandoffPayload {
  items: HandoffItem[];
  promotionCodes: Array<{ code: string }>;
  url?: string;
}

interface BuildExportPayloadOptions {
  url?: string;
  cart?: CartState;
}

interface CartDrawerElement extends Element {
  close(): void;
}

/**
 * @typedef {Object} HandoffItem
 * @property {string} id - SKU of the product
 * @property {number} qty - Quantity
 * @property {boolean} [gwp] - Whether this is a gift with purchase item
 */

/**
 * @typedef {Object} HandoffPayload
 * @property {HandoffItem[]} items - Cart items to export
 * @property {Array<{code: string}>} promotionCodes - Active discount codes
 * @property {string} [url] - The intended destination URL on the sister site
 */

/**
 * Formats raw Shopify cart items into the handoff payload shape
 * @param {Object} cart - The Shopify cart object from getCart()
 * @returns {HandoffItem[]} Array of handoff items
 */
export function formatCartItems(cart: CartState): HandoffItem[] {
  return cart.items.map((item) => {
    const exportItem: HandoffItem = {
      id: String(item.sku),
      qty: item.quantity,
    };
    if (item.properties?._gwp === 'true' || item.properties?._gwp === true) {
      exportItem.gwp = true;
    }
    return exportItem;
  });
}

/**
 * Gets all items from the current cart formatted for handoff
 * @returns {Promise<HandoffItem[]>} Array of handoff items
 */
export async function getCartItemsForExport(): Promise<HandoffItem[]> {
  try {
    const cart = await getCart();
    return formatCartItems(cart);
  } catch (error) {
    console.error('[Shared Cart] Error fetching cart items:', error);
    return [];
  }
}

/**
 * Builds the export payload from current cart state
 * @param {Object} [options]
 * @param {string} [options.url] - The intended destination URL on the sister site
 * @param {Object} [options.cart] - Pre-fetched Shopify cart object (avoids redundant fetch)
 * @returns {Promise<HandoffPayload>}
 */
export async function buildExportPayload({
  url,
  cart,
}: BuildExportPayloadOptions = {}): Promise<HandoffPayload> {
  let items;
  let discountCodes;

  if (cart) {
    items = formatCartItems(cart);
    discountCodes = cart.discount_codes?.filter((x) => x.applicable)?.map((x) => x.code) || [];
  } else {
    items = await getCartItemsForExport();
    discountCodes = await getActiveCartDiscounts();
  }

  const payload: HandoffPayload = {
    promotionCodes: discountCodes.map((code) => ({ code })),
    items,
  };

  if (url) {
    payload.url = url;
  }

  console.info('[Shared Cart] Built export payload:', payload);
  return payload;
}

/**
 * Attempts to clear the cart with retry logic
 * @param {number} [retries=2] - Number of retry attempts after initial failure
 * @returns {Promise<void>}
 */
export async function clearCartWithRetry(retries = 2): Promise<void> {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      await clearCart();
      return;
    } catch (error) {
      if (attempt === retries) {
        throw error;
      }
      console.warn(`[Shared Cart] clearCart attempt ${attempt + 1} failed, retrying...`);
    }
  }
}

/**
 * Returns the environment prefix (e.g. 'qa', 'staging') from the current hostname.
 * Production hostnames return an empty string.
 * @returns {string}
 */
export function getEnvironmentPrefix(): string {
  const hostname = window.location.hostname;
  if (hostname.startsWith('qa.')) return 'qa';
  if (hostname.startsWith('staging.')) return 'staging';
  return '';
}

/**
 * Maps a sister-site URL to the current environment.
 * On qa.loopha.com, theordinary.com becomes qa.theordinary.com.
 * On production, URLs pass through unchanged.
 * @param {string} href - The sister-site URL to map
 * @returns {string}
 */
export function mapToEnvironmentUrl(href: string): string {
  const prefix = getEnvironmentPrefix();
  if (!prefix) return href;

  try {
    const url = new URL(href, window.location.href);
    const baseDomain = SISTER_SITE_DOMAINS.find(
      (d) => url.hostname === d || url.hostname.endsWith(`.${d}`),
    );
    if (baseDomain) {
      url.hostname = `${prefix}.${baseDomain}`;
    }
    return url.toString();
  } catch {
    return href;
  }
}

/**
 * Checks whether a URL points to a known sister site
 * @param {string} href - The URL to check
 * @returns {boolean}
 */
export function isSisterSiteUrl(href: string): boolean {
  try {
    const url = new URL(href, window.location.href);
    const ignoredDomains = [...CART_EXPORT_IGNORED_DOMAINS, window.location.hostname];
    if (ignoredDomains.some((d) => url.hostname === d || url.hostname.endsWith(`.${d}`)))
      return false;
    return SISTER_SITE_DOMAINS.some((d) => url.hostname === d || url.hostname.endsWith(`.${d}`));
  } catch {
    return false;
  }
}

/**
 * Handles click on sister-site links to export cart
 * @param {MouseEvent} event - The click event
 * @param {HTMLAnchorElement} link - The anchor element that was clicked
 * @returns {Promise<void>}
 */
export async function handleBrandClick(
  event: Pick<MouseEvent, 'preventDefault'>,
  link: HTMLAnchorElement,
): Promise<void> {
  event.preventDefault();
  document.querySelector<CartDrawerElement>('cart-drawer')?.close();

  // Map the link to the correct environment (qa/staging/production)
  const mappedHref = mapToEnvironmentUrl(link.href);

  // Fetch cart once — used for export payload and backup
  let cart;
  try {
    cart = await getCart();
  } catch (error) {
    console.error('[Shared Cart] Error fetching cart:', error);
    window.location.assign(mappedHref);
    return;
  }

  // If no items, just navigate normally
  if (!cart.items.length) {
    console.info('[Shared Cart] No items to export, navigating to link');
    window.location.assign(mappedHref);
    return;
  }

  // Add locale prefix to the payload URL if not already present
  const locale = window?.Shopify?.localization_custom?.locale || '';
  const payloadUrl = new URL(mappedHref);
  if (locale && !payloadUrl.pathname.startsWith(`/${locale}`)) {
    payloadUrl.pathname =
      payloadUrl.pathname === '/' ? `/${locale}/` : `/${locale}${payloadUrl.pathname}`;
  }

  const payload = await buildExportPayload({
    url: payloadUrl.toString(),
    cart,
  });
  const encoded = base64UrlEncode(JSON.stringify(payload));

  // Backup cart for back-navigation restore (errors caught internally)
  const discountCodes = payload.promotionCodes.map((p) => p.code);
  backupCart(cart, discountCodes);

  // Clear the source cart before redirecting
  try {
    await clearCartWithRetry();
    console.info('[Shared Cart] Cart cleared before brand switch');
  } catch (error) {
    console.warn(
      '[Shared Cart] Failed to clear cart after retries, proceeding with redirect:',
      error,
    );
  }

  // Build destination URL — always redirect to the sister site's /cart-import
  const url = new URL(mappedHref);
  url.search = '';
  url.pathname = locale ? `/${locale}/cart-import` : '/cart-import';
  url.searchParams.set('handoff', encoded);

  console.info('[Shared Cart] Redirecting with cart handoff:', url.toString());
  window.location.assign(url.toString());
}

/**
 * Initializes cart export functionality via event delegation.
 * Intercepts clicks on any <a> pointing to a sister-site domain.
 */
export function initCartExport(): void {
  document.addEventListener('click', async (event) => {
    const link = (event.target as Element).closest<HTMLAnchorElement>('a[href]');
    if (!link || !isSisterSiteUrl(link.href)) return;
    try {
      await handleBrandClick(event, link);
    } catch (error) {
      console.error('[Shared Cart] Export failed, navigating normally:', error);
      window.location.assign(mapToEnvironmentUrl(link.href));
    }
  });

  console.info('[Shared Cart] Delegated export handler attached');
}
